// Central registry for /barcode-guides/ articles.
// Used by the index page, sitemap, and "related guides" cross-links.
export type GuideMeta = {
  slug: string;
  path: string; // e.g. "/barcode-guides/upc-vs-ean"
  title: string;
  shortTitle: string;
  /** One-line subtitle shown under the title — the article's lede in 10–14 words. */
  dek: string;
  /**
   * Intro paragraph under the guide's H1 (40 to 60 words): the dek plus one
   * sentence. Falls back to the dek. The guides index cards keep the dek.
   */
  intro?: string;
  description: string; // meta description + index card blurb
  keyword: string; // primary long-tail target
  readingMinutes: number;
  /** ISO date string of the last meaningful content update. */
  lastUpdated: string;
};

export const GUIDES: GuideMeta[] = [
  {
    slug: "upc-vs-ean",
    path: "/barcode-guides/upc-vs-ean",
    title: "UPC vs EAN: What's Actually Different?",
    shortTitle: "UPC vs EAN",
    dek: "Two flavours of the same idea, separated by one digit and forty years of geography.",
    intro:
      "Two flavours of the same idea, separated by one digit and forty years of geography. It explains how a 12-digit UPC-A becomes a 13-digit EAN-13, where each one is required, and how to confirm the check digit with the free ReadBarcode GTIN Validator before a value reaches a retailer feed.",
    description:
      "The practical difference between UPC-A and EAN-13 — digit counts, country prefixes, GS1 ownership, and when each one is required at retail.",
    keyword: "UPC vs EAN",
    readingMinutes: 6,
    lastUpdated: "2026-05-21",
  },
  {
    slug: "what-is-a-gtin",
    path: "/barcode-guides/what-is-a-gtin",
    title: "What Is a GTIN? GTIN-8, 12, 13 and 14 Explained",
    shortTitle: "What is a GTIN?",
    dek: "The global identifier behind every UPC, EAN, ITF-14 and ISBN — what each length means and where the check digit comes from.",
    intro:
      "The global identifier behind every UPC, EAN, ITF-14 and ISBN — what each length means and where the check digit comes from. The free GTIN Validator on ReadBarcode checks the mod-10 check digit of any GTIN-8, GTIN-12, GTIN-13 or GTIN-14 in your browser, and the Check Digit Calculator computes a missing one.",
    description:
      "GTIN is the global identifier behind UPC, EAN, ITF-14 and ISBN. Here's what each length means, where the check digit comes from, and how to use it.",
    keyword: "what is a GTIN",
    readingMinutes: 7,
    lastUpdated: "2026-05-21",
  },
  {
    slug: "1d-vs-2d-barcodes",
    path: "/barcode-guides/1d-vs-2d-barcodes",
    title: "1D vs 2D Barcodes: When To Use Each",
    shortTitle: "1D vs 2D barcodes",
    dek: "Capacity, scanner support, packaging footprint and the GS1 Sunrise-2027 migration story.",
    intro:
      "Capacity, scanner support, packaging footprint and the GS1 Sunrise-2027 migration story. It compares the two side by side, explains which scanners read each type, and includes a worked GS1 Digital Link example you can open in the free ReadBarcode decoder to see the GTIN, batch and expiry split out.",
    description:
      "1D barcodes encode a number. 2D barcodes encode a payload. Here's a practical comparison of capacity, scanner support, packaging footprint, and migration paths.",
    keyword: "1D vs 2D barcodes",
    readingMinutes: 6,
    lastUpdated: "2026-05-21",
  },
  {
    slug: "code-128-explained",
    path: "/barcode-guides/code-128-explained",
    title: "Code 128 Explained: Subsets, FNC1 and Real-World Use",
    shortTitle: "Code 128 explained",
    dek: "The workhorse 1D barcode behind shipping labels and GS1-128 — subsets, FNC1, and what to watch for when decoding it.",
    intro:
      "The workhorse 1D barcode behind shipping labels and GS1-128 — subsets, FNC1, and what to watch for when decoding it. The free ReadBarcode Barcode Generator makes Code 128 symbols from up to 80 printable ASCII characters and downloads them as PNG or SVG, and the bulk generator handles up to 200 values at once.",
    description:
      "Code 128 is the workhorse 1D barcode behind shipping labels and GS1-128. Learn its three subsets, the FNC1 modifier, and what to watch for when decoding it.",
    keyword: "Code 128 explained",
    readingMinutes: 7,
    lastUpdated: "2026-05-21",
  },
  {
    slug: "barcode-formats",
    path: "/barcode-guides/barcode-formats",
    title: "Barcode Formats: A Plain-English Field Guide",
    shortTitle: "Barcode formats",
    dek: "A reference of the symbologies you'll actually meet in retail, logistics and healthcare — what each one encodes and where it shows up.",
    intro:
      "A reference of the symbologies you'll actually meet in retail, logistics and healthcare — what each one encodes and where it shows up. The free ReadBarcode barcode reader decodes the 1D and 2D symbols shown here, including Code 128, QR Code, Data Matrix and PDF417, from a camera or an uploaded image.",
    description:
      "A reference of common barcode formats — UPC-A, EAN-13, EAN-8, Code 128, Code 39, ITF-14, QR, Data Matrix and PDF417 — with what each one encodes.",
    keyword: "barcode formats",
    readingMinutes: 8,
    lastUpdated: "2026-05-21",
  },
  {
    slug: "sunrise-2027-explained",
    path: "/barcode-guides/sunrise-2027-explained",
    title: "Sunrise 2027 Explained: The 2D-at-POS Transition for Retail",
    shortTitle: "Sunrise 2027 explained",
    dek: "What GS1's 2D-at-POS commitment means for brands, retailers and the consumer-pack barcodes shipping today.",
    intro:
      "What GS1's 2D-at-POS commitment means for brands, retailers and the consumer-pack barcodes shipping today. It explains what brands and retailers must change, and shows how to build a GS1 Digital Link QR code or Data Matrix with the free ReadBarcode generator and check the printed payload with the decoder.",
    description:
      "GS1's Sunrise 2027 commits global retail POS to accept 2D barcodes alongside 1D EAN/UPC by end of 2027 — what brands and scanners need to do now.",
    keyword: "Sunrise 2027",
    readingMinutes: 8,
    lastUpdated: "2026-05-21",
  },
  {
    slug: "gs1-digital-link",
    path: "/barcode-guides/gs1-digital-link",
    title: "GS1 Digital Link Explained: One QR for POS and Phone",
    shortTitle: "GS1 Digital Link",
    dek: "How one 2D barcode carries the retail GTIN for the till and a working URL for the phone — URL syntax, AI placement and resolver basics.",
    intro:
      "How one 2D barcode carries the retail GTIN for the till and a working URL for the phone — URL syntax, AI placement and resolver basics. It includes a worked example you can open in the GS1 Digital Link decoder to see each Application Identifier split out.",
    description:
      "GS1 Digital Link lets one 2D barcode carry the retail GTIN AND a working URL for the phone. Here's URL syntax, Application Identifiers and deployment.",
    keyword: "GS1 Digital Link",
    readingMinutes: 7,
    lastUpdated: "2026-05-21",
  },
  {
    slug: "check-digits-explained",
    path: "/barcode-guides/check-digits-explained",
    title: "How Check Digits Work: The Math Behind GTIN, ISBN and SSCC",
    shortTitle: "Check digits explained",
    dek: "GS1 mod-10, ISBN-10 mod-11, and Code 128's mod-103 in plain English — why one trailing digit catches every single-digit typo.",
    intro:
      "GS1 mod-10, ISBN-10 mod-11, and Code 128's mod-103 in plain English — why one trailing digit catches every single-digit typo. The mod-10 and mod-11 methods are worked through step by step, and the GS1 example opens in the free ReadBarcode Check Digit Calculator so you can repeat the math on your own codes.",
    description:
      "Every UPC, EAN, GTIN, ISBN and SSCC ends in a check digit: GS1 mod-10 in plain English, ISBN-10's mod-11 cousin, and why it catches every single-digit typo.",
    keyword: "barcode check digit",
    readingMinutes: 6,
    lastUpdated: "2026-05-21",
  },
];

export const GUIDES_BY_SLUG: Record<string, GuideMeta> = Object.fromEntries(
  GUIDES.map((g) => [g.slug, g]),
);

export const GUIDES_INDEX_PATH = "/barcode-guides";
